import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useStore } from '@tanstack/react-form';

import { type OptionType } from '@/api/common';
import {
  type InstanceDetailDataType,
  type InstanceTaskEventSettingsType,
} from '@/api/organization';
import { Checkbox, CheckboxGroup, useTypedAppFormContext } from '@/components/Form';
import { Field, FieldLabel } from '@/components/Form/fields';
import { Text } from '@/components/typography';

type Props = {
  taskOptions?: OptionType[];
  disabled?: boolean;
  isEditMode?: boolean;
  isHidden?: boolean;
  value?: InstanceTaskEventSettingsType | null;
  onChange?: (value: InstanceTaskEventSettingsType) => void;
};

const CompanyNonDigitalTasksList = ({
  taskOptions,
  disabled = false,
  isEditMode = false,
  isHidden = false,
  value,
  onChange,
}: Props) => {
  const form = useTypedAppFormContext({ defaultValues: {} as InstanceDetailDataType });
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  if (isHidden) {
    return isEditMode ? null : <Text variant="body1" italic>{t`none`}</Text>;
  }

  if (!isEditMode) {
    const selectedItems = value?.offlineTasksSpecific
      .map((id) => taskOptions?.find((option) => option.id === id)?.name)
      .concat(value?.offlineEventsAll ? t`Events` : []);

    if (!selectedItems?.length) {
      return <Text variant="body1" italic>{t`none`}</Text>;
    }

    return <Text variant="body1">{selectedItems?.join(', ')}</Text>;
  }

  const atLeastOneChecked = !!(value && value.offlineTasksSpecific.length > 0);
  const allChecked = taskOptions?.length === value?.offlineTasksSpecific.length;

  const handleAllTasksChange = (checked: boolean) => {
    if (checked) {
      onChange?.({
        offlineTasksAll: true,
        offlineEventsAll: value?.offlineEventsAll ?? false,
        offlineTasksSpecific: taskOptions?.map((option) => option.id) ?? [],
      });
    } else {
      onChange?.({
        offlineTasksAll: false,
        offlineEventsAll: value?.offlineEventsAll ?? false,
        offlineTasksSpecific: [],
      });
    }
  };

  const handleAllEventsChange = (checked: boolean) => {
    onChange?.({
      offlineTasksAll: value?.offlineTasksAll ?? false,
      offlineEventsAll: checked,
      offlineTasksSpecific: value?.offlineTasksSpecific ?? [],
    });
  };

  const handleTasksChange = (values: string[]) => {
    const isAllSelected = values.length === taskOptions?.length;
    onChange?.({
      offlineTasksAll: isAllSelected,
      offlineEventsAll: value?.offlineEventsAll ?? false,
      offlineTasksSpecific: values,
    });
  };

  return (
    <div className="flex max-h-60 max-w-90 flex-col gap-y-2 overflow-auto rounded-sm border border-gray-400 px-3 py-2">
      <Field orientation="horizontal">
        <Checkbox
          id="task-events-all-tasks"
          indeterminate={atLeastOneChecked && !allChecked}
          checked={allChecked}
          disabled={disabled || isSubmitting}
          onCheckedChange={handleAllTasksChange}
        />
        <FieldLabel htmlFor="task-events-all-tasks">
          <Trans>Tasks</Trans>
        </FieldLabel>
      </Field>
      <CheckboxGroup
        className="pl-5"
        orientation="vertical"
        disabled={disabled || isSubmitting}
        value={value?.offlineTasksSpecific}
        onValueChange={handleTasksChange}
      >
        {taskOptions?.map((taskType) => (
          <Field key={taskType.id} orientation="horizontal">
            <Checkbox id={`task-events-${taskType.id}`} value={taskType.id} />
            <FieldLabel htmlFor={`task-events-${taskType.id}`}>{taskType.name}</FieldLabel>
          </Field>
        ))}
      </CheckboxGroup>
      <Field orientation="horizontal">
        <Checkbox
          id="task-events-all-events"
          checked={value?.offlineEventsAll}
          disabled={disabled || isSubmitting}
          onCheckedChange={handleAllEventsChange}
        />
        <FieldLabel htmlFor="task-events-all-events">
          <Trans>Events</Trans>
        </FieldLabel>
      </Field>
    </div>
  );
};

export default CompanyNonDigitalTasksList;
